import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { TRACKER_FIND_PERSON_RETRY_DAYS } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { Spinner } from "@/components/Spinner/Spinner";
import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";
import { AsyncProcessingStatus, AsyncProcessingType } from "@/enum/list.enum";

import { ListPerson } from "../../schema/list";
import { isTrackerDummyProspect } from "./dummyProspect";
import { useFindLeadForProspect } from "./useFindLeadForProspect";
import { trackerFindPersonRowState } from "./trackerFindPersonRow";
import { useTrackerFindPersonEnabled } from "./useTrackerFindPersonEnabled";

// Name-column cell for dummy prospects: a "Select prospect" button that opens the global person
// search (single-select, pre-scoped to the row's company) and replaces the placeholder with the
// chosen person — via the tracker-aware mutation for tracker rows, the plain replace otherwise.
export const SelectProspectCell: FunctionComponent<{ row: ListPerson }> = ({ row }) => {
	const { t } = useTranslation();
	const { companyName, companyDomain, showSearchModal, setShowSearchModal, handleConfirm } =
		useFindLeadForProspect(row);
	const isTrackerRow = isTrackerDummyProspect(row);
	const findPersonOnSignal = useTrackerFindPersonEnabled(row.list?.id, !isTrackerRow);
	const searchState = isTrackerRow
		? trackerFindPersonRowState(row, findPersonOnSignal)
		: ({ kind: "NONE" } as const);
	const tf = (key: string, options?: Record<string, unknown>) =>
		t(`defineIcps.trackerFindPerson.row.${key}`, options);

	// The tracker list's automatic person search is running for this company (PREC-4399). The
	// button stays: a person picked by hand ends the search without a charge.
	const searchingLine = searchState.kind === "SEARCHING" && (
		<div className="mb-1 flex flex-col text-sm text-text-meta">
			<span className="flex items-center gap-2">
				<Spinner color="#6b7280" />
				{tf("searching")}
			</span>
			<span className="text-xs">
				{tf("searchingSince", { time: format(searchState.since, "dd.MM.yyyy HH:mm") })}
			</span>
		</div>
	);

	// While a bulk find-person search is running for this row, show a spinner instead of the button.
	const isFindingPerson =
		row.asyncProcessingType === AsyncProcessingType.FIND_PERSON &&
		(row.asyncProcessingStatus === AsyncProcessingStatus.PENDING ||
			row.asyncProcessingStatus === AsyncProcessingStatus.PROCESSING);

	if (isFindingPerson) {
		return (
			<div
				className="flex items-center gap-2 text-sm text-text-meta"
				onClick={(e) => e.stopPropagation()}
			>
				<Spinner color="#6b7280" />
				{t("companyEnrich.findPersonTitle")}
			</div>
		);
	}

	return (
		<div onClick={(e) => e.stopPropagation()}>
			{searchingLine}
			<Button
				title={
					row.prospect?.name === "SELECT" && searchState.kind !== "NOT_FOUND"
						? t("list.selectProspect")
						: t("list.personNotFound")
				}
				onClick={(e) => {
					e.stopPropagation();
					setShowSearchModal(true);
				}}
			/>
			{searchState.kind === "NOT_FOUND" && (
				<div className="mt-1 text-xs text-text-meta">
					{tf("notFoundSince", {
						date: searchState.searchedAt ? format(searchState.searchedAt, "dd.MM.yyyy") : "",
						days: TRACKER_FIND_PERSON_RETRY_DAYS,
					})}
				</div>
			)}
			{searchState.kind === "NO_CREDITS" && (
				<div className="mt-1 text-xs text-text-meta">
					{tf("noCredits", {
						date: searchState.at ? format(searchState.at, "dd.MM.yyyy") : "",
					})}
				</div>
			)}
			{searchState.kind === "OFF" && <div className="mt-1 text-xs text-text-meta">{tf("off")}</div>}
			{showSearchModal && (
				<GlobalSearchModal
					singleSelect
					confirmLabel={t("list.selectProspect")}
					initialFilterCompany={
						companyDomain ? { name: companyName, domain: companyDomain } : undefined
					}
					isOpened={showSearchModal}
					setIsOpened={setShowSearchModal}
					onConfirmSelection={handleConfirm}
				/>
			)}
		</div>
	);
};

export {
	isDummyProspect,
	isNonTrackerDummyProspect,
	isPlaceholderProspectName,
	isTrackerDummyProspect,
} from "./dummyProspect";
